import { catchWithLog, catchWithRedactedLog, degraded } from 'helper/zod';
import type { z } from 'zod';

import type { FieldError, FieldErrorType, MonitoredField } from '../monitored';
import type { GeoPositionFE } from '../vehicle';

/**
 * Shared building blocks for mapping a generated REST client's wire types onto the domain model.
 *
 * Extracted from vehicleFromRestV2 so the mission mapper does not have to repeat them. The point
 * of every helper here is the same: never throw on unexpected input. An unrecognised enum value or
 * a malformed timestamp degrades one field, leaving the rest of the object usable.
 */

export const PARSING_ERROR_TYPE = 'ERROR_TYPE_PARSING_ERROR';

/** Synthetic envelope standing in for a field whose value could not be parsed. */
export const parsingErrorField = () => ({
    error: { type: PARSING_ERROR_TYPE, updated_at: new Date().toISOString() },
});

/** Optional wire field: an unparseable value degrades to undefined. */
export const safeOptional = <S extends z.ZodTypeAny>(schema: S, label: string) =>
    catchWithLog(schema.nullish(), undefined as unknown as z.infer<S> | null | undefined, label);

/** Monitored wire field: an unparseable value degrades to an envelope carrying PARSING_ERROR. */
export const safeField = <S extends z.ZodTypeAny>(schema: S, label: string) =>
    catchWithLog(schema.nullish(), parsingErrorField as unknown as () => z.infer<S> | null | undefined, label);

/**
 * Optional wire field whose value must never be logged, for personal data. Same degradation as
 * {@link safeOptional}, but the console line carries no value and no Zod issue — a Zod issue
 * echoes the offending input, which here is a name, phone number or email address.
 */
export const safeRedactedOptional = <S extends z.ZodTypeAny>(schema: S, label: string) =>
    catchWithRedactedLog(schema.nullish(), undefined as unknown as z.infer<S> | null | undefined, label);

/**
 * Staging serves `{}` for an empty collection where both `ms-client` and the api repo declare an
 * array, which the array schema would log as a degradation on every empty parent. Only the EMPTY
 * object normalises: a populated object still fails loudly.
 */
export const emptyObjectToArray = (value: unknown) =>
    value != null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0 ? [] : value;

/**
 * Maps a wire list one entry at a time. This is the degradation rule the whole mapper layer rests
 * on: one malformed record is dropped and logged, never allowed to discard the rest of the list.
 * Six mappers hand-rolled this loop before it lived here.
 *
 * `mapOne` may return `undefined` to reject an entry without throwing — the hub mapper does that
 * for a hub with no usable coordinates. Pass `redactEntries` for payloads carrying personal data:
 * the log then names only the label, never the offending record.
 */
export const mapListPerEntry = <T>(
    raw: unknown,
    mapOne: (entry: unknown) => T | undefined,
    label: string,
    options: { redactEntries?: boolean } = {}
): T[] => {
    const { redactEntries = false } = options;

    if (!Array.isArray(raw)) {
        const message = degraded(label, `expected an array, received ${typeof raw}`);
        if (redactEntries) {
            console.error(message);
        } else {
            console.error(message, raw);
        }
        return [];
    }

    return raw.reduce<T[]>((acc, entry) => {
        try {
            const mapped = mapOne(entry);
            if (mapped !== undefined) {
                acc.push(mapped);
            }
        } catch (error) {
            if (redactEntries) {
                console.error(degraded(`${label}[]`, 'one entry dropped, entry redacted'));
            } else {
                console.error(degraded(`${label}[]`, 'one entry dropped'), entry, error);
            }
        }
        return acc;
    }, []);
};

/**
 * Maps a wire list of `{ id, name }` references onto the domain shape, dropping an entry with no
 * id and defaulting a missing name to `''`. The logged entry is an `{ id, name }` pair, which
 * carries no personal data.
 */
export const toNamedRefs = (
    wire: ReadonlyArray<{ id?: string | null; name?: string | null }> | null | undefined,
    label: string
): { id: string; name: string }[] =>
    (wire ?? []).reduce<{ id: string; name: string }[]>((acc, entry) => {
        if (!entry.id) {
            console.error(degraded(label, 'reference dropped, no usable id'), entry);
            return acc;
        }
        acc.push({ id: entry.id, name: entry.name ?? '' });
        return acc;
    }, []);

/**
 * Wire keys whose string values are timestamps. sanitizeWire normalises these to ISO so the
 * library schemas, which validate with z.iso.datetime(), accept values that are valid instants
 * written in another format.
 */
const DATE_KEYS = new Set([
    'updated_at',
    'created_at',
    'sent_at',
    'raised_at',
    'latest_vehicle_update',
    'timestamp',
    'started_at',
    'resolved_at',
    'planned_arrival_time',
    'actual_start_time',
    'actual_arrival_time',
    'last_repeated_at',
    'eta',
]);

/**
 * Builds a wire sanitiser over a specific set of timestamp keys, so each mapper normalises only
 * the keys its own API actually carries. The traversal itself is identical for every caller; only
 * the key list is API metadata.
 *
 * Returns a closure rather than taking the key set as a second parameter on purpose: the sanitiser
 * is handed straight to `Array.prototype.map` and `z.preprocess`, both of which pass extra
 * positional arguments (`(value, index, array)` and `(value, ctx)`) that would bind to it.
 */
export const makeSanitizeWire = (dateKeys: ReadonlySet<string>) => {
    const sanitize = (input: unknown): unknown => {
        if (Array.isArray(input)) {
            return input.map(sanitize);
        }
        if (input && typeof input === 'object') {
            const out: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(input)) {
                if (value === null) {
                    continue;
                }
                if (typeof value === 'string' && dateKeys.has(key)) {
                    const parsed = new Date(value);
                    out[key] = Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
                } else {
                    out[key] = sanitize(value);
                }
            }
            return out;
        }
        return input;
    };
    return sanitize;
};

/**
 * Prepares a raw wire payload for validation: drops nulls, since the generated schemas model
 * "absent" as an optional key rather than an explicit null, and normalises timestamp strings.
 */
export const sanitizeWire = makeSanitizeWire(DATE_KEYS);

/**
 * Maps a prefixed wire enum member onto a domain union, e.g.
 * 'MISSION_STATE_VALUE_EXECUTING' -> 'EXECUTING'.
 * Anything unrecognised, including the wire's own *_UNSPECIFIED, becomes 'UNKNOWN'.
 */
export const fromEnum = <T extends string>(raw: unknown, prefix: string, known: readonly T[]): T => {
    if (typeof raw !== 'string' || !raw.startsWith(prefix)) {
        return 'UNKNOWN' as T;
    }
    const member = raw.slice(prefix.length);
    return (known as readonly string[]).includes(member) ? (member as T) : ('UNKNOWN' as T);
};

/** fromEnum for wire enums that carry no prefix. */
export const fromPlainEnum = <T extends string>(raw: unknown, known: readonly T[]): T => {
    if (typeof raw !== 'string') {
        return 'UNKNOWN' as T;
    }
    return (known as readonly string[]).includes(raw) ? (raw as T) : ('UNKNOWN' as T);
};

export const toDate = (raw: unknown): Date | undefined => {
    if (typeof raw !== 'string' || raw.trim() === '') {
        return;
    }
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

export const KNOWN_FIELD_ERROR_TYPES: readonly string[] = [
    'UNSPECIFIED',
    'UNKNOWN',
    'UNAVAILABLE',
    'TIMEOUT',
    'NOT_CONFIGURED',
    'PARSING_ERROR',
];

const FIELD_ERROR_TYPE_PREFIX = 'ERROR_TYPE_';

export const toFieldErrorType = (raw: unknown): FieldErrorType =>
    fromEnum(raw, FIELD_ERROR_TYPE_PREFIX, KNOWN_FIELD_ERROR_TYPES) as unknown as FieldErrorType;

export interface WireEnvelope<V> {
    ttl?: number | null;
    value?: V | null;
    error?: { type?: string | null; updated_at?: string | null } | null;
}

export const toFieldError = (raw: WireEnvelope<unknown>['error']): FieldError | null => {
    if (raw == null) {
        return null;
    }
    return {
        type: toFieldErrorType(raw.type),
        updatedAt: toDate(raw.updated_at) ?? null,
    };
};

/**
 * Turns a wire `{ ttl, value, error }` envelope into a normalised MonitoredField.
 * A value that cannot be mapped is reported as absent while ttl and error are preserved, so
 * consumers can still tell a parse failure apart from a field that simply has no value.
 */
export const toMonitoredField = <V, D>(
    envelope: WireEnvelope<V> | null | undefined,
    mapValue: (value: V) => D | undefined,
    updatedAtOf: (value: V) => string | null | undefined
): MonitoredField<D> => {
    if (envelope == null) {
        return {};
    }
    const ttl = envelope.ttl ?? null;
    const error = toFieldError(envelope.error);
    const rawValue = envelope.value;
    if (rawValue == null) {
        return { ttl, value: null, error };
    }
    const mapped = mapValue(rawValue);
    if (mapped === undefined) {
        return { ttl, value: null, error };
    }
    return {
        ttl,
        value: { value: mapped, updatedAt: toDate(updatedAtOf(rawValue)) ?? null },
        error,
    };
};

/**
 * The wire position shape, loose enough to serve every caller: the vehicles API marks all four
 * keys optional, the missions API marks latitude/longitude required. Both are assignable here, and
 * the runtime `typeof` guards below mean the looser typing cannot change behaviour for either.
 */
export interface WireGeoPosition {
    latitude?: number | null;
    longitude?: number | null;
    heading_degrees?: number | null;
    altitude_meters?: number | null;
}

/**
 * Renames the wire's position keys onto the domain shape. A fix without usable coordinates yields
 * undefined rather than a half-built position, so callers can drop it outright.
 */
export const toGeoPosition = (raw: WireGeoPosition | null | undefined): GeoPositionFE | undefined => {
    if (raw == null || typeof raw.latitude !== 'number' || typeof raw.longitude !== 'number') {
        return;
    }
    const position: GeoPositionFE = { lat: raw.latitude, lng: raw.longitude };
    if (typeof raw.heading_degrees === 'number') {
        position.heading = raw.heading_degrees;
    }
    if (typeof raw.altitude_meters === 'number') {
        position.altitudeMeters = raw.altitude_meters;
    }
    return position;
};

/** Date -> ISO string form of a MonitoredField, for slices that must stay serialisable. */
export const serializeMonitoredField = <T>(field: MonitoredField<T>): MonitoredField<T, string> => ({
    ...field,
    value:
        field.value == null
            ? field.value
            : {
                  ...field.value,
                  updatedAt: field.value.updatedAt?.toISOString() ?? null,
              },
    error:
        field.error == null
            ? field.error
            : {
                  ...field.error,
                  updatedAt: field.error.updatedAt?.toISOString() ?? null,
              },
});
